import {
	REGED_USER_ID_MAX_LENGTH,
	useAccountUsesRegEd,
	useRegEdIntegrationQuery,
	useRegEdMyUserMapQuery,
	useRegEdUserMapDeleteMutation,
	useRegEdUserMapUpsertMutation,
} from '@Queries/RegEdCompliance';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Button } from '../../../components-v2/Button';
import { Field, FieldLabel } from '../../../components-v2/Field';
import { Input } from '../../../components-v2/Input';
import { RemoveRepCodeModal } from '../../../components/integrations/RemoveRepCodeModal';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const ManageRegEdCompliance = ({ className, styles }: IProps) => {
	const userSession = useUserSession();
	const impersonationContext = userSession.impersonationContext;
	const usesRegEd = useAccountUsesRegEd(impersonationContext);
	// `usesRegEd` fail-closes while loading, so redirecting before it settles bounces an enabled advisor.
	const { isLoading: isGateLoading } = useRegEdIntegrationQuery({ impersonationContext });
	const { data: userMap, isLoading } = useRegEdMyUserMapQuery({ impersonationContext, enabled: usesRegEd });

	const history = useHistory();
	const [isRemoveModalOpen, setIsRemoveModalOpen] = React.useState(false);
	const [repCode, setRepCode] = React.useState('');
	// Syncs to nothing too: a mapping deleted elsewhere must clear the field or Save silently re-creates it.
	React.useEffect(() => {
		setRepCode(userMap?.regEdUserId ?? '');
	}, [userMap?.regEdUserId]);

	const upsertMutation = useRegEdUserMapUpsertMutation({
		onError: () =>
			toast.error('An error occurred while saving your Rep Code. If the problem persists, please contact support.'),
		onSuccess: () => toast.success('Your Rep Code has been saved.'),
	});

	const removeMutation = useRegEdUserMapDeleteMutation({
		onError: () =>
			toast.error('An error occurred while removing your Rep Code. If the problem persists, please contact support.'),
		onSuccess: () => {
			toast.success('Your Rep Code has been removed.');
			history.push('/integrations');
		},
	});

	if (isGateLoading || isLoading) {
		return <LoadingSpinner type='large' />;
	}
	if (!usesRegEd) {
		return <Redirect to='/integrations' />;
	}

	const onSaveClicked = () => {
		const value = repCode.trim();
		if (!value) {
			return;
		}
		upsertMutation.mutate({ regEdUserId: value, impersonationContext });
	};

	const onRemoveConfirmed = () => removeMutation.mutate({ impersonationContext });

	const savedRepCode = (userMap?.regEdUserId ?? '').trim();
	const isUnchanged = repCode.trim() === savedRepCode;
	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} component-class ${className || ''}`}>
			<header className={css(styleSheet.header)}>
				<h1 className={css(baseStyleSheet.breadcrumbTitle)}>RegEd Compliance</h1>
				<small className={css(integrationsStyleSheet.bodyText)}>
					Levitate routes your marketing to your firm's RegEd system for compliance review. Your Rep Code is how your
					firm identifies you — filings Levitate sends are attributed to it. Your firm's compliance team can tell you
					yours.
				</small>
			</header>

			<div className={css(integrationsStyleSheet.signInContainer, styleSheet.signInContainer)}>
				<Field>
					<FieldLabel htmlFor='repCode'>Rep Code</FieldLabel>
					<Input
						id='repCode'
						maxLength={REGED_USER_ID_MAX_LENGTH}
						onChange={ev => setRepCode(ev.target.value.slice(0, REGED_USER_ID_MAX_LENGTH))}
						placeholder='Enter your Rep Code'
						type='text'
						value={repCode}
					/>
					{savedRepCode ? (
						<div className={css(styleSheet.savedState)}>
							<SuccessCheckIcon />
							<span>Filings are attributed to {savedRepCode}.</span>
						</div>
					) : (
						<div className={css(styleSheet.hint)}>Up to {REGED_USER_ID_MAX_LENGTH} characters.</div>
					)}
				</Field>
				<div className={css(styleSheet.center)}>
					{savedRepCode ? (
						<Button
							disabled={removeMutation.isPending}
							isLoading={removeMutation.isPending}
							onClick={() => setIsRemoveModalOpen(true)}
							styles={[styleSheet.removeLink]}
							variant='link'
						>
							Remove
						</Button>
					) : null}
					<Button
						disabled={upsertMutation.isPending || !repCode.trim() || isUnchanged}
						isLoading={upsertMutation.isPending}
						onClick={onSaveClicked}
					>
						Save
					</Button>
				</div>
			</div>

			<RemoveRepCodeModal
				isOpen={isRemoveModalOpen}
				onClose={() => setIsRemoveModalOpen(false)}
				onRemove={onRemoveConfirmed}
			/>
		</div>
	);
};
